import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GenPdf from '../GenPdf';
import Header from '../Header/Header';
import NumDossFacAmendment from '../NumDossFacAmendment';
import LoadingOverlay from "../LoadingOverlay";
import DocMenu from "../DocMenu";
import ListTable from './FacAmendmentRecap/ListTable';
import { getContractAmendmentInfo, getTauxhRea, genFacAmendmentRecap, getFacAmendmentRecap, getAmendmentLetter } from "./Utils";
import { fetchAllFacAmendmentReminderByDoss } from "../../redux/actions/reminderFacAmendmentActions";
import { fetchEcheancesByNdossier } from "../../redux/actions/echeancesActions";
import { fetchContrat } from "../../redux/actions/contratActions";
import { changeTitleName, changePdfFileName } from "../../redux/actions/emailAction";
import "../../Styles/FactureAmendment/FacAmendmentRecap.scss";

const FacAmendmentRecap = () => {
  const dispatch = useDispatch();

  // Redux state avec useSelector
  const devisrea = useSelector(state => state.devis.devisrea);
  const dossierValue = useSelector(state => state.devis.dossierValue);
  const dossiers = useSelector(state => state.dossier.items);
  const contacts = useSelector(state => state.contact.items);
  const contractFacAmendments = useSelector(state => state.facAmendment.contractFacAmendments);
  const facAmendmentsByDossIndex = useSelector(state => state.facAmendment.facAmendmentsByDossIndex);
  const contractFacAmendmentIndex = useSelector(state => state.facAmendment.contractFacAmendmentIndex);
  const facAmendmentIndex = useSelector(state => state.facAmendment.facAmendmentIndex);
  const facAmendmentsRecapByDoss = useSelector(state => state.facAmendment.facAmendmentsRecapByDoss);
  const echeances = useSelector(state => state.echeances.itemNdossier);
  const reminders = useSelector(state => state.reminderFacAmendment.items);

  // Local state
  const [facturesData, setFacturesData] = useState([]);
  const [tauxh, setTauxh] = useState(0);
  const [totalCumulativeAmount, setTotalCumulativeAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [refFacRecap, setRefFacRecap] = useState("");
  const [bdcFacRecap, setBdcFacRecap] = useState("");
  const [file, setFile] = useState(null);
  const [isFileOpen, setIsFileOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  const indexAmendmentLetter = getAmendmentLetter(contractFacAmendmentIndex);

  const getCumulativeAmount = useCallback((indexItem, facAmendmentIndex) => {
    if (indexItem - 1 > facAmendmentIndex) return 0;
    return facAmendmentsByDossIndex.slice(0, indexItem).reduce((acc, item) => acc + item.total_ht, 0);
  }, [facAmendmentsByDossIndex]);

  const handleGetDateReminder = useCallback((refFacture, firstReminder, index) => {
    if (index + 1 > facAmendmentIndex) return "";
    const formatRefFacture = `${refFacture} `;
    const currentReminders = reminders
      .filter(item => item.first_reminder === firstReminder && item.numclient.includes(formatRefFacture));
    const formatedDate = currentReminders.length > 0 ? new Date(currentReminders[0].date_reminder).toLocaleDateString('en-CA') : "";
    return formatedDate;
  }, [reminders, facAmendmentIndex]);

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-CA') : null;

  const handleChangeDateToMonth = (dateString) => {
    const months = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    const date = new Date(dateString);
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    const month = months[monthIndex];
    return `${month} ${year}`;
  };

  const loadData = useCallback(async () => {
    if (!dossierValue) return;

    setDisableButton(true);
    dispatch(changePdfFileName(""));

    const tauxhValue = getTauxhRea(devisrea, dossierValue);
    const bdcFacRecapValue = facAmendmentsByDossIndex[facAmendmentIndex - 1]?.bdc_facture ?? "";

    let dates = [];
    let amountEch = 0;

    const currentContractAmendmentInfos = await getContractAmendmentInfo(dossierValue, contractFacAmendmentIndex);

    if (currentContractAmendmentInfos) {
      dates = currentContractAmendmentInfos.dates !== undefined ? JSON.parse(currentContractAmendmentInfos.dates) : [];
      amountEch = facAmendmentsByDossIndex.length > 0 ? parseFloat(((currentContractAmendmentInfos.hours * facAmendmentsByDossIndex[0].pu_ht) / dates.length).toFixed(2)) : 0;
    }

    const facturesDataArray = dates.map((item, index) => {
      const currentFacAmendment = facAmendmentsByDossIndex[index];
      const isBeforeCurrentIndex = index < facAmendmentIndex;
      const isAfterCurrentIndex = index > facAmendmentIndex - 1;

      const factureNumber = isBeforeCurrentIndex && currentFacAmendment ? `${indexAmendmentLetter}-${currentFacAmendment.nfacture.split(" ")[1]}` : "";

      return {
        month_ech: `${handleChangeDateToMonth(item.dateech)} FAC: ${factureNumber} ${indexAmendmentLetter}R${index + 1}`,
        date_ech: formatDate(item.dateech),
        total_ht: currentFacAmendment?.total_ht ?? amountEch,
        cumulative_amount: getCumulativeAmount(index + 1, facAmendmentIndex - 1),
        first_reminder: handleGetDateReminder(`${indexAmendmentLetter}R${index + 1}`, 1, index),
        second_reminder: handleGetDateReminder(`${indexAmendmentLetter}R${index + 1}`, 0, index),
        payment_date: formatDate(isAfterCurrentIndex ? null : currentFacAmendment?.payment_date),
        validation: !isAfterCurrentIndex && currentFacAmendment?.payment_date !== null && currentFacAmendment?.payment_date !== undefined
      };
    });

    const totalCumulativeAmountValue = facturesDataArray.reduce((maxValue, item) => {
      return Math.max(maxValue, item.cumulative_amount);
    }, 0);

    const totalAmountValue = facturesDataArray
      .slice(0, facAmendmentIndex)
      .reduce((acc, item) => acc + (!item.validation ? item.total_ht : 0), 0);

    setFacturesData(facturesDataArray);
    setTotalCumulativeAmount(totalCumulativeAmountValue);
    setTotalAmount(totalAmountValue);
    setTauxh(tauxhValue);
    setRefFacRecap("");
    setBdcFacRecap(bdcFacRecapValue);
    setFile(null);
    setDisableButton(false);
  }, [
    dossierValue,
    contractFacAmendments,
    contractFacAmendmentIndex,
    facAmendmentsByDossIndex,
    facAmendmentIndex,
    facAmendmentsRecapByDoss,
    reminders,
    dispatch
  ]);

  useEffect(() => {
    dispatch(changePdfFileName(""));
    const fetchDossierData = async () => {
      if (!dossierValue) return;

      await dispatch(fetchContrat(dossierValue));
      await dispatch(fetchEcheancesByNdossier(dossierValue));
    };

    fetchDossierData();
  }, [dossierValue])

  useEffect(() => {
    const fetchData = async () => {
      if (!dossierValue || !contractFacAmendmentIndex) return;

      await dispatch(fetchAllFacAmendmentReminderByDoss(dossierValue, contractFacAmendmentIndex));
    };

    fetchData();
  }, [dossierValue, contractFacAmendmentIndex]);

  useEffect(() => {
    loadData();
  }, [facAmendmentsByDossIndex, facAmendmentIndex]);

  const handleChangeValues = (e) => {
    const { name, value } = e.target;

    if (name === 'refFacRecap') setRefFacRecap(value);
    else if (name === 'bdcFacRecap') setBdcFacRecap(value);
  };

  const initializeValues = () => {
    loadData();
    closeDoc();
  };

  const handleSaveFacAmendmentReacp = async () => {
    setDisableButton(true);
    const id_facturation = facAmendmentsByDossIndex.filter(item => item.numclient.includes(`${indexAmendmentLetter}R${facAmendmentIndex} `))[0].id;

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ndossier: dossierValue,
        amendment_number: contractFacAmendmentIndex,
        id_facturation,
        data: JSON.stringify(facturesData),
        cumulative_amount: totalCumulativeAmount,
        total_amount: totalAmount,
        ref_facture: refFacRecap,
        bdc_facture: bdcFacRecap,
      })
    };

    await fetch("/api/facamendment/createFacAmendmentRecap", requestOptions)
      .then((res) => res.json())
      .catch((err) => {
        console.log(err);
      });

    setDisableButton(false);
  };

  const handleGetNfacture = () => {
    let nfactures = "";

    for (let i = 0; i < facAmendmentIndex; i++) {
      const newNfacture = facAmendmentsByDossIndex[i].nfacture.split(" ").join(`/${indexAmendmentLetter}-`);
      nfactures += facAmendmentsByDossIndex[i].payment_date === null ? nfactures.length > 0 ? " & " + newNfacture : newNfacture : "";
    }

    return nfactures;
  };

  const handleGenFacAmendmentRecap = async () => {
    setFile(null);
    setDisableButton(true);

    const sortedFac = facAmendmentsByDossIndex.filter(item => item.ndossier == dossierValue)
      .sort((a, b) => a.numclient.localeCompare(b.numclient));

    const numclient = sortedFac.filter(item => item.numclient.includes(`FR ${dossierValue}/${indexAmendmentLetter}R${facAmendmentIndex} `))[0].numclient;
    const cumulative_total = Math.max(
      ...facturesData
        .filter((item) => typeof item.cumulative_amount === 'number')
        .map((item) => item.cumulative_amount || 0)
    );

    const amount_to_pay = facturesData.reduce((total, item, index) => {
      return index < facAmendmentIndex && !item.validation ? total + item.total_ht : total;
    }, 0);

    const data = facturesData.map(item => {
      return {
        ...item,
        total_ht: item.total_ht.toFixed(2),
        validation: item.validation ? "100%" : "",
        cumulative_amount: item.cumulative_amount ? item.cumulative_amount.toFixed(2) : "",
      };
    });

    const currentContractAmendmentInfos = await getContractAmendmentInfo(dossierValue, contractFacAmendmentIndex);

    if (!currentContractAmendmentInfos || !currentContractAmendmentInfos.dates) {
      console.error("Données d'amendement de contrat invalides");
      setDisableButton(false);
      return;
    }

    const nbEchs = JSON.parse(currentContractAmendmentInfos.dates).length;
    const echs = `${nbEchs} échéances ${currentContractAmendmentInfos.payment_method === 26 ? "bimestrielles" : "mensuelles"}`;
    const amout_ech_ht = (tauxh * currentContractAmendmentInfos.hours) / nbEchs;

    const currentDossier = dossiers.filter(item => item.ndossier === dossierValue)[0];
    const refFacture = `${indexAmendmentLetter}R${facAmendmentIndex}`;
    const maitreDouvrage = contacts.filter(item => item.idcontact === currentDossier.idcontact)[0];

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ndossier: dossierValue,
        contractIndex: contractFacAmendmentIndex,
        dossier_op: currentDossier,
        date_signature: echeances.datepgcp,
        maitre_ouvrage: JSON.stringify(maitreDouvrage),
        ref_fac_recap: refFacRecap,
        bdc_fac_recap: bdcFacRecap,
        data,
        cumulative_total: cumulative_total.toFixed(2),
        amount_to_pay: amount_to_pay.toFixed(2),
        amount_to_pay_tva: (amount_to_pay * 0.2).toFixed(2),
        amount_to_pay_ttc: (amount_to_pay * 1.2).toFixed(2),
        totalht: (tauxh * currentContractAmendmentInfos.hours).toFixed(2),
        totalttc: ((tauxh * currentContractAmendmentInfos.hours) * 1.2).toFixed(2),
        amout_ech_ht: amout_ech_ht.toFixed(2),
        amout_ech_ttc: (amout_ech_ht * 1.2).toFixed(2),
        duration: currentContractAmendmentInfos.duration,
        echs,
        numclient,
        refFacture,
        nfacture: handleGetNfacture(),
      })
    };

    let blob = await genFacAmendmentRecap(requestOptions);
    if (blob) {
      setFile(blob);
    }
    setDisableButton(false);
    setIsFileOpen(true);
  };


  const handleShowPdf = async () => {
    const refFacture = `${indexAmendmentLetter}R${facAmendmentIndex}`;
    setFile(null);
    setDisableButton(true);

    let response = await getFacAmendmentRecap(dossierValue, contractFacAmendmentIndex, refFacture, 'pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      setIsFileOpen(true);

      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName));
    }
    setDisableButton(false);
  };

  const handleGetPdfFile = async () => {
    const refFacture = `${indexAmendmentLetter}R${facAmendmentIndex}`;
    setFile(null);

    let response = await getFacAmendmentRecap(dossierValue, refFacture, 'pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);

      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName));
    }
  };

  const handleGetWordFile = async () => {
    const refFacture = `${indexAmendmentLetter}R${facAmendmentIndex}`;
    setFile(null);

    let response = await getFacAmendmentRecap(dossierValue, refFacture, 'word');

    if (response.blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setFile(response.blob);

      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName));
    }
  };

  const closeDoc = () => {
    setIsFileOpen(false);
  };

  const handleChangeTitleName = () => {
    dispatch(changeTitleName(`Facture Avenant Récapitulative ${indexAmendmentLetter}R${facAmendmentIndex}`));
  };
  
  return (
    <div className="fac-recap-amendment">
      {disableButton && <LoadingOverlay />}
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
      />
      <h1 className="title">
        Facture Avenant Récapitulative
      </h1>
      <NumDossFacAmendment type="facAmendment" />
      <div className="data">
        <ListTable
          facturesData={facturesData}
          totalCumulativeAmount={totalCumulativeAmount}
          totalAmount={totalAmount}
          refFacRecap={refFacRecap}
          bdcFacRecap={bdcFacRecap}
          disableButton={disableButton}
          handleChangeValues={handleChangeValues}
          handleSaveFacAmendmentReacp={handleSaveFacAmendmentReacp}
          handleGenFacAmendmentRecap={handleGenFacAmendmentRecap}
          handleShowPdf={handleShowPdf}
          initializeValues={initializeValues}
        />
        <GenPdf
          isOpen={isFileOpen}
          file={file}
          closeDoc={closeDoc}
          scale={file !== null ? 1 : 0.5}
        />
      </div>
    </div>
  );
};

export default FacAmendmentRecap;
